// src/components/customers/EditCustomerDrawer.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Form,
  Input,
  Switch,
  Button,
  Space,
  Collapse,
  message,
  Select,
  Typography,
} from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  UserOutlined,
} from '@ant-design/icons';

import {
  updateCustomer,
  type CustomerDTO,
  type CreateCustomerRequest,
} from '../../services/customers';
import { listSectors, type SectorDTO } from '../../services/sector';
import { useAuth } from '../../context/AuthContext';

const { TextArea } = Input;
const { Text } = Typography;

type Props = {
  open: boolean;
  onClose: () => void;
  customer: CustomerDTO | null;
  onUpdated: (customer: CustomerDTO) => void;
};

type FormValues = Partial<CreateCustomerRequest> & {
  sectorId?: number | null;
  // campi utente associato
  userEmail?: string;
  userFirstName?: string;
  userLastName?: string;
  userPassword?: string;        // opzionale: solo se ADMIN/PJM e toggle attivo
  changePassword?: boolean;     // switch per mostrare campo password
};

const EditCustomerDrawer: React.FC<Props> = ({
  open,
  onClose,
  customer,
  onUpdated,
}) => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [sectors, setSectors] = useState<Array<{ label: string; value: number }>>([]);
  const changePassword = Form.useWatch('changePassword', form);

  // Ruolo utente corrente per abilitare la modifica password
  const { user } = useAuth();
  const canEditPassword = useMemo(() => {
    const r = String(user?.role || '').toUpperCase();
    return r === 'ADMIN' || r === 'PJM';
  }, [user?.role]);

  // Carica settori quando apro
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const list = await listSectors(true);
        setSectors((list || []).map((s: SectorDTO) => ({ label: s.name, value: s.id })));
      } catch {
        setSectors([]);
      }
    })();
  }, [open]);

  // Prefill form quando apro / cambia customer
  useEffect(() => {
    if (!open || !customer) return;
    form.setFieldsValue({
      businessName: customer.businessName,
      vatNumber: customer.vatNumber || '',
      taxCode: customer.taxCode || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      postalCode: customer.postalCode || '',
      province: customer.province || '',
      country: customer.country || 'IT',
      notes: customer.notes || '',
      credentials: customer.credentials || '',
      sectorId: (customer as any).sector?.id ?? undefined,
      // dati utente associato (se disponibili nel DTO)
      userEmail: customer.userEmail || '',
      userFirstName: (customer as any).userFirstName || '',
      userLastName: (customer as any).userLastName || '',
      changePassword: false,
    });
  }, [open, customer, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    if (!customer) return;
    try {
      const values = await form.validateFields();

      // Validazioni password se richiesta e permessa
      let userPassword: string | undefined;
      if (canEditPassword && values.changePassword) {
        if (!values.userPassword || values.userPassword.length < 6) {
          throw new Error('La nuova password deve avere almeno 6 caratteri.');
        }
        userPassword = values.userPassword;
      }

      setLoading(true);

      // Costruisco payload di update
      const payload: Partial<CreateCustomerRequest> & {
        sectorId?: number | null;
        userPassword?: string;
        userEmail?: string;
        userFirstName?: string;
        userLastName?: string;
      } = {
        businessName: values.businessName!,
        vatNumber: values.vatNumber ?? customer.vatNumber ?? undefined,
        taxCode: values.taxCode ?? customer.taxCode ?? undefined,
        email: values.email ?? customer.email ?? undefined,
        phone: values.phone ?? customer.phone ?? undefined,
        address: values.address ?? customer.address ?? undefined,
        city: values.city ?? customer.city ?? undefined,
        postalCode: values.postalCode ?? customer.postalCode ?? undefined,
        province: values.province ?? customer.province ?? undefined,
        country: values.country ?? customer.country ?? 'IT',
        notes: values.notes ?? customer.notes ?? undefined,
        credentials: values.credentials?.trim() || undefined,
        sectorId: values.sectorId ?? null,
        // campi utente
        userEmail: values.userEmail || undefined,
        userFirstName: values.userFirstName || undefined,
        userLastName: values.userLastName || undefined,
        userPassword, // presente solo se ADMIN/PJM + changePassword
      };

      const updated = await updateCustomer(customer.id!, payload);
      message.success('Cliente aggiornato con successo');
      onUpdated(updated);
      handleClose();
    } catch (err: any) {
      if (err?.errorFields) return; // errori di validazione AntD
      message.error(err?.message || 'Errore durante il salvataggio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={
        <Space>
          <UserOutlined />
          <span>Modifica Cliente</span>
        </Space>
      }
      open={open}
      onClose={handleClose}
      width={640}
      destroyOnClose
      styles={{ body: { paddingBottom: 80 } }}
      extra={
        <Space>
          <Button onClick={handleClose} icon={<CloseOutlined />}>
            Annulla
          </Button>
          <Button type="primary" onClick={handleSubmit} loading={loading} icon={<SaveOutlined />}>
            Salva
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        disabled={loading}
      >
        {/* Ragione sociale + P.IVA + Settore */}
        <Form.Item
          name="businessName"
          label="Ragione sociale"
          rules={[{ required: true, message: 'La ragione sociale è obbligatoria' }]}
        >
          <Input placeholder="Es. Tech Solutions Srl" />
        </Form.Item>

        <Space.Compact block style={{ gap: 12 }}>
          <Form.Item name="vatNumber" label="P.IVA" style={{ flex: 1 }}>
            <Input placeholder="IT01234567890" />
          </Form.Item>

          <Form.Item name="sectorId" label="Settore" style={{ flex: 1 }}>
            <Select
              allowClear
              showSearch
              placeholder="Seleziona settore"
              options={sectors}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Space.Compact>

        {/* Contatti + indirizzo */}
        <Collapse
          destroyInactivePanel={false}
          expandIconPosition="end"
          style={{ marginTop: 8, marginBottom: 16 }}
          items={[
            {
              key: 'contact-address',
              label: 'Contatti e indirizzo',
              forceRender: true,
              children: (
                <>
                  <Space.Compact block style={{ gap: 12 }}>
                    <Form.Item
                      name="email"
                      label="Email"
                      style={{ flex: 1 }}
                      rules={[{ type: 'email', message: 'Email non valida' }]}
                    >
                      <Input placeholder="contatti@azienda.it" />
                    </Form.Item>
                    <Form.Item name="phone" label="Telefono" style={{ flex: 1 }}>
                      <Input placeholder="+39 02 1234567" />
                    </Form.Item>
                  </Space.Compact>

                  <Form.Item name="address" label="Indirizzo">
                    <Input placeholder="Via Roma 1" />
                  </Form.Item>

                  <Space.Compact block style={{ gap: 12 }}>
                    <Form.Item name="city" label="Città" style={{ flex: 1 }}>
                      <Input placeholder="Milano" />
                    </Form.Item>
                    <Form.Item name="postalCode" label="CAP" style={{ flex: 1 }}>
                      <Input placeholder="20100" />
                    </Form.Item>
                  </Space.Compact>

                  <Space.Compact block style={{ gap: 12 }}>
                    <Form.Item name="province" label="Provincia" style={{ flex: 1 }}>
                      <Input placeholder="MI" />
                    </Form.Item>
                    <Form.Item name="country" label="Paese" style={{ flex: 1 }}>
                      <Input placeholder="IT" />
                    </Form.Item>
                  </Space.Compact>
                </>
              ),
            },
          ]}
        />



        {/* Note */}
        <Form.Item name="notes" label="Note">
          <TextArea placeholder="Note interne..." autoSize={{ minRows: 3 }} />
        </Form.Item>

        <Form.Item name="credentials" label="Credenziali">
          <TextArea placeholder="Accessi, password o indicazioni riservate..." autoSize={{ minRows: 3 }} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default EditCustomerDrawer;
